// src/elements/captions.ts
import { log } from "../utils/debug";
import { parseSRT } from "../utils/parseSRT";
import { Z } from "@/utils/constants";

const CC_PREF_KEY = "sc:cc-pref";
function readCcPrefBool(): boolean | null {
  try {
    const v = localStorage.getItem(CC_PREF_KEY);
    if (v === "1" || v === "true") return true;
    if (v === "0" || v === "false") return false;
    if (v === "on") return true;
    if (v === "off") return false;
  } catch {}
  return null;
}
function writeCcPrefBool(on: boolean): void {
  try {
    localStorage.setItem(CC_PREF_KEY, on ? "1" : "0");
    // keep legacy key in sync for backward compatibility
    localStorage.setItem("storycore-cc", on ? "on" : "off");
  } catch {}
}

// ---------- Global caption preference ----------
let GLOBAL_CC_ENABLED: boolean = (() => {
  const pref = readCcPrefBool();
  if (pref === true) return true;
  if (pref === false) return false;
  const legacy = localStorage.getItem("storycore-cc");
  if (legacy === "off") return false;
  if (legacy === "on") return true;
  return true; // default ON
})();

function syncGlobalFromStorage() {
  const pref = readCcPrefBool();
  if (pref === true) GLOBAL_CC_ENABLED = true;
  if (pref === false) GLOBAL_CC_ENABLED = false;
}

export function areCaptionsEnabledGlobally(): boolean {
  return GLOBAL_CC_ENABLED;
}

export function setGlobalCaptionsEnabled(on: boolean): void {
  GLOBAL_CC_ENABLED = !!on;
  writeCcPrefBool(GLOBAL_CC_ENABLED);
  try {
    document.dispatchEvent(
      new CustomEvent("sc:captions-global", { detail: { enabled: GLOBAL_CC_ENABLED } })
    );
  } catch {}
}

export type CaptionOptions = {
  mode?: "on" | "off" | "auto";          // default "auto"
  position?: "bottom" | "mid-bottom";    // default "mid-bottom"
  maxWidthPct?: number;                   // default 90
  safeBottomPx?: number;                  // default 64 (toolbar safe area)
  zIndex?: number;                        // default 800 (above text 500, below HUD 3000)
};

export type CaptionHandle = {
  mount(container: HTMLElement): void;
  attachToVideo(video: HTMLVideoElement): void;
  loadVTT(url: string): Promise<void>;
  loadSRT(url: string): Promise<void>;
  loadIfExists(url: string): Promise<boolean>;
  setMode(mode: "on" | "off" | "auto", opts?: { source?: "user" | "system" }): void;
  setPosition(pos: "bottom" | "mid-bottom"): void;
  show(): void;
  hide(): void;
  destroy(): void;
};

type SRTCue = { start: number; end: number; text: string };

export function createCaptions(opts: CaptionOptions = {}): CaptionHandle {
  let {
    mode = (() => {
      const pref = readCcPrefBool();
      if (pref === true) return "on";
      if (pref === false) return "off";
      const legacy = localStorage.getItem("storycore-cc");
      if (legacy === "off") return "off";
      if (legacy === "on") return "on";
      return "on";
    })(),
    position = "mid-bottom",
    maxWidthPct = 90,
    // Use a slightly larger safe bottom padding for mobile home indicator clearance
    safeBottomPx = 72,
    zIndex = Z.CAPTIONS,
  } = opts;

  let root: HTMLDivElement | null = null;
  let label: HTMLDivElement | null = null;
  let mounted = false;
  let rafId: number | null = null;

  let video: HTMLVideoElement | null = null;
  let usingNativeTracks = false;
  let nativeTrack: TextTrack | null = null;

  let srtCues: SRTCue[] = [];
  let currentIdx = -1;

  // Host section + external wiring
  let sectionRef: HTMLElement | null = null;
  let ccEvtHandler: ((e: any) => void) | null = null;
  let globalCcEvtHandler: ((e: any) => void) | null = null;
  let attrObserver: MutationObserver | null = null;
  let lastEffective: "on" | "off" = "on";

  function isSectionActive(sec: HTMLElement | null) {
    if (!sec) return false;
    const rev = sec.getAttribute('data-revealed');
    if (rev === '1') return true;
    // Fallback heuristics if data-revealed is not set
    const idx = sec.getAttribute('data-index') || sec.getAttribute('data-section-index');
    if (idx === '1') return true;
    return false;
  }

  const notifyAvailable = () => {
    try {
      if (video) {
        video.dispatchEvent(new CustomEvent('sc:captions-available', { bubbles: true }));
        try { log('[CAPTIONS] availability event dispatched'); } catch {}
      }
    } catch {}
  };

  const setVisible = (show: boolean) => {
    if (!root) return;
    root.style.opacity = show ? "1" : "0";
    root.style.visibility = show ? "visible" : "hidden";
  };

  const setCaptionText = (text: string) => {
    if (!label || !root) return;
    label.textContent = text;

    const show = mode !== "off" && text.trim().length > 0;

    // Also expose state to theme CSS and consumers
    label.classList.toggle("visible", show);

    // Cache the last text on the hosting section so HUD toggles can re-apply visibility instantly
    const hostSection = root.closest("section") as HTMLElement | null;
    if (hostSection) (hostSection as any).__lastCaptionText = text;

    // Inline visibility so we are not blocked by legacy CSS
    setVisible(show);
  };

  const ensureDom = () => {
    if (root) return;
    root = document.createElement("div");
    root.className = "sc-captions-layer absolute";
    // Accessibility semantics
    root.setAttribute('role', 'region');
    root.setAttribute('aria-live', 'polite');
    // Minimal inline style, move visual styling to CSS
    root.style.zIndex = String(zIndex);
    root.style.pointerEvents = "none";
    root.style.opacity = "0";
    root.style.visibility = "hidden";
    root.style.transition = "opacity 160ms ease";

    // Positioning
    applyPosition(position, safeBottomPx);

    label = document.createElement("div");
    label.className = "sc-caption";
    // Accessibility atomic update for label
    label.setAttribute('aria-atomic', 'true');
    // No inline visual styles for label; all moved to CSS

    root.appendChild(label);
    // Start hidden if mode is off (or until a cue appears)
    // if (mode === "off") root.classList.add("hidden");
  };

  function applyPosition(pos: "bottom" | "mid-bottom", safePx: number) {
    if (!root) return;
    if (pos === "bottom") {
      root.style.top = "";
      root.style.bottom = `${safePx}px`;
      root.style.left = "50%";
      root.style.transform = "translateX(-50%)";
    } else {
      // mid-bottom: float it above toolbars a bit
      root.style.bottom = "";
      root.style.top = "65%";
      root.style.left = "50%";
      root.style.transform = "translate(-50%, -50%)";
    }
  }

  function mount(container: HTMLElement) {
    ensureDom();
    if (!mounted && root) {
      container.appendChild(root);
      mounted = true;

      // Capture host section reference for event scoping
      try {
        sectionRef = (container.closest('section') as HTMLElement) || (container as HTMLElement);
      } catch { sectionRef = container as HTMLElement; }

      // Listen for external CC changes (e.g., HUD button)
      if (!ccEvtHandler) {
        ccEvtHandler = (e: any) => {
          const d = (e && e.detail) || {};
          const desiredOn = !!d.active;
          const targetSec: HTMLElement | null = d.section || null;

          // If event is targeted to a particular section, ignore others
          if (targetSec && sectionRef && targetSec !== sectionRef) return;

          // If it's a broadcast (no section), only apply when our section is active
          if (!targetSec && !isSectionActive(sectionRef)) return;

          const desired = desiredOn ? 'on' : 'off';
          if (desired === mode) return;
          setMode(desired as any);
        };
        try { document.addEventListener('sc:cc-change', ccEvtHandler as any, true); } catch {}
      }

      // Keep already-mounted caption handles in sync when the global
      // captions preference changes from another reel's CC toggle.
      if (!globalCcEvtHandler) {
        globalCcEvtHandler = (e: any) => {
          const enabled = !!e?.detail?.enabled;
          setMode(enabled ? "on" : "off");
        };
        try { document.addEventListener("sc:captions-global", globalCcEvtHandler as any, true); } catch {}
      }

      // Observe direct attribute changes as a safety net
      try {
        if (!attrObserver && sectionRef) {
          attrObserver = new MutationObserver((records) => {
            for (const r of records) {
              if (r.type === 'attributes' && r.attributeName === 'data-cc-hidden') {
                const hidden = sectionRef!.getAttribute('data-cc-hidden') === '1';
                const desired = hidden ? 'off' : 'on';
                if (desired !== mode) setMode(desired as any);
              }
            }
          });
          attrObserver.observe(sectionRef, { attributes: true, attributeFilter: ['data-cc-hidden'] });
        }
      } catch {}

      // Hide until first cue arrives to avoid first-paint flash
      setVisible(false);
      // viewport safe-area handling
      try {
        if (window.visualViewport) {
          const vv = window.visualViewport;
          const sync = () => {
            // when keyboard/toolbars change, nudge bottom safe area
            if (position === "bottom" && root) {
              root.style.bottom = `${safeBottomPx + Math.max(0, window.innerHeight - vv.height)}px`;
            }
          };
          vv.addEventListener("resize", sync);
          vv.addEventListener("scroll", sync);
        }
      } catch {}
    }
  }

  function setMode(next: "on" | "off" | "auto", opts?: { source?: "user" | "system" }) {
    // Respect stored global preference; only explicit user toggles update it.
    syncGlobalFromStorage();
    const fromUser = opts?.source === "user";
    if (fromUser) {
      setGlobalCaptionsEnabled(next !== "off");
    }

    mode = next;
    const section = root ? (root.closest("section") as HTMLElement | null) : null;
    const turningOn = next !== "off" && areCaptionsEnabledGlobally();
    const effective: "on" | "off" = turningOn ? "on" : "off";

    // No-op if effective state hasn't changed; prevents recursion storms.
    if (effective === lastEffective && !fromUser) return;
    lastEffective = effective;

    if (nativeTrack) {
      try { nativeTrack.mode = turningOn ? "showing" : "hidden"; } catch {}
    }

    // Reflect intent to CSS (theme rules can read this)
    if (section) section.setAttribute("data-cc-hidden", turningOn ? "0" : "1");

    // Broadcast a single authoritative event so HUD/others can sync.
    // Use active based on the effective state (after global preference).
    try {
      document.dispatchEvent(new CustomEvent('sc:cc-change', {
        detail: { active: effective === "on", section, video }
      }));
    } catch {}

    if (!root) return;

    if (!turningOn) {
      // Hide immediately when turning off
      setVisible(false);
      if (label) label.textContent = "";
      return;
    }

    // Turning on: show if we have text, otherwise stay ready/visible
    const current = (label?.textContent ?? "");
    if (current.trim().length > 0) {
      setVisible(true);
    } else {
      // No text yet; keep layer ready but hidden until first cue arrives
      setVisible(false);
    }
  }

  function setPosition(pos: "bottom" | "mid-bottom") {
    position = pos;
    applyPosition(position, safeBottomPx);
  }

  function attachToVideo(v: HTMLVideoElement) {
    video = v;
    try { log('[CAPTIONS] attachToVideo:', { src: v.currentSrc || v.src, tracks: (v.textTracks && (v.textTracks as any).length) || 0 }); } catch {}
    usingNativeTracks = false;
    currentIdx = -1;
    nativeTrack = null;

    // Prefer native textTracks when available
    try {
      const tracks = Array.from(video.textTracks || []);
      const textTrack = tracks.find(t => t.kind === "subtitles" || t.kind === "captions");
      if (textTrack) {
        usingNativeTracks = true;
        nativeTrack = textTrack;
        try { log('[CAPTIONS] Using native TextTrack(s). kind:', textTrack.kind, 'mode→', (mode !== "off" ? "showing" : "hidden")); } catch {}
        textTrack.mode = (mode !== "off") ? "showing" : "hidden";
        textTrack.oncuechange = () => {
          if (!label) return;
          const cueList = (textTrack.cues || []) as any;
          if (!cueList || cueList.length === 0) {
            label.textContent = "";
            return;
          }
          // Grab the last active cue (if multiple)
          let activeText = "";
          try {
            const active = Array.from(textTrack.activeCues || []);
            if (active.length) activeText = (active[active.length - 1] as any).text || "";
          } catch {
            // Some browsers don't expose activeCues consistently; fallback:
            const t = video!.currentTime;
            for (let i = 0; i < cueList.length; i++) {
              const c = cueList[i];
              if (t >= c.startTime && t <= c.endTime) { activeText = c.text; break; }
            }
          }
          setCaptionText(activeText);
          if (mode !== "off") setVisible((activeText || "").trim().length > 0);
        };
        notifyAvailable();
        return;
      }
    } catch {}

    try { if (!usingNativeTracks) log('[CAPTIONS] No native tracks found — falling back to SRT loop'); } catch {}

    // Fallback to our own timeupdate loop using SRT cues
    const tick = () => {
      if (!video || !label) { rafId = requestAnimationFrame(tick); return; }
      const t = video.currentTime;
      const idx = findCueIndex(t, srtCues, currentIdx);
      if (idx !== currentIdx) {
        currentIdx = idx;
        const newText = idx === -1 ? "" : srtCues[idx].text;
        setCaptionText(newText);
        // If CC is on and we have text, make sure layer is visible
        if (mode !== "off") setVisible(newText.trim().length > 0);
      }
      rafId = requestAnimationFrame(tick);
    };
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(tick);
  }

  function findCueIndex(t: number, cues: SRTCue[], hint: number): number {
    if (!cues.length) return -1;
    // small locality search around hint
    if (hint >= 0) {
      const c = cues[hint];
      if (t >= c.start && t <= c.end) return hint;
      if (hint + 1 < cues.length && t <= cues[hint + 1].end) {
        for (let i = hint; i < Math.min(cues.length, hint + 12); i++) {
          const x = cues[i];
          if (t >= x.start && t <= x.end) return i;
        }
      }
    }
    // fallback linear (cues are small)
    for (let i = 0; i < cues.length; i++) {
      const c = cues[i];
      if (t >= c.start && t <= c.end) return i;
    }
    return -1;
  }

  async function loadIfExists(url: string): Promise<boolean> {
    try { log('[CAPTIONS] HEAD check for', url); } catch {}
    try {
      const head = await fetch(url, { method: 'HEAD' });
      if (!head.ok) { try { log('[CAPTIONS] No captions at', url, 'status', head.status); } catch {}; return false; }
      try { log('[CAPTIONS] Found captions at', url, '→ loading SRT'); } catch {}
      await loadSRT(url);
      return true;
    } catch {
      return false;
    }
  }

  async function loadVTT(_url: string) {
    // Placeholder: relying on native <track> VTT for now.
    // We can add manual VTT parsing if needed later.
    return;
  }

  async function loadSRT(url: string) {
    try { log('[CAPTIONS] Fetching SRT:', url); } catch {}
    const res = await fetch(url);
    if (!res.ok) return;
    const text = await res.text();
    const cues = parseSRT(text) as any as SRTCue[];
    srtCues = cues || [];
    try { log('[CAPTIONS] SRT loaded. cues:', srtCues.length); } catch {}
    if (srtCues.length > 0) notifyAvailable();
    currentIdx = -1;
  }

  function show() { setMode(mode === "off" ? "on" : mode); }
  function hide() { setMode("off"); }

  function destroy() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    try { if (ccEvtHandler) document.removeEventListener('sc:cc-change', ccEvtHandler as any, true); } catch {}
    ccEvtHandler = null;
    try { if (globalCcEvtHandler) document.removeEventListener("sc:captions-global", globalCcEvtHandler as any, true); } catch {}
    globalCcEvtHandler = null;
    try { attrObserver?.disconnect(); } catch {}
    attrObserver = null;
    try { root?.remove(); } catch {}
    // best-effort detach handle marker if one was set by the mount wrapper
    try { (sectionRef as any).__captionsHandle = null; } catch {}
    try { if (nativeTrack) (nativeTrack as any).oncuechange = null; } catch {}
    nativeTrack = null;
    root = null; label = null; video = null; sectionRef = null;
  }

  ensureDom();
  return { mount, attachToVideo, loadVTT, loadSRT, loadIfExists, setMode, setPosition, show, hide, destroy };
}

export type MountCaptionsOptions = {
  section: HTMLElement;
  reelFrame?: HTMLElement | null;
  video: HTMLVideoElement;
  attrs?: Record<string, any>;
  baseUrl: string;
};

/**
 * Mount captions overlay and wire it to a video element.
 * - If a `.vtt` is provided (via `captions` or `captions-src`), we attach a native &lt;track&gt; to the video.
 * - If an `.srt` is provided, we parse it and drive our overlay manually.
 * - If no source is provided, this is a no‑op beyond mounting the overlay; it will still
 *   display if a native track already exists on the video.
 *
 * Returns the CaptionHandle so callers can show/hide/update or destroy later.
 */
export function mountCaptionsElement(opts: MountCaptionsOptions): CaptionHandle | null {
  const { section, reelFrame, video, attrs = {}, baseUrl } = opts;
  if (!section || !video) return null;

  try { log('[CAPTIONS] mountCaptionsElement', { sectionId: section.id, reelFrame: !!reelFrame }); } catch {}

  // Ensure we only ever have ONE captions layer per section.
  // Remove any pre-existing layers (legacy mounts or previous renders).
  try {
    const existingLayers = section.querySelectorAll(".sc-captions-layer, .srt-caption");
    existingLayers.forEach(el => { try { el.remove(); } catch {} });
  } catch {}

  // Default placement: inside the reel frame if present; otherwise the section.
  const container = (reelFrame ?? section) as HTMLElement;

  // Derive behavior from attrs
  const rawSrc = String(attrs["captions-src"] ?? attrs["captions"] ?? "").trim();
  const rawMode = String(attrs["captions-mode"] ?? "").toLowerCase();
  const mode: "on" | "off" | "auto" | undefined =
    rawMode === "on" || rawMode === "off" || rawMode === "auto" ? (rawMode as any) : undefined;

  try { log('[CAPTIONS] attrs', { rawSrc, rawMode, mode }); } catch {}

  // Prefer a slightly higher position when nested over a reel frame
  const position: "bottom" | "mid-bottom" = reelFrame ? "mid-bottom" : "bottom";

  const handle = createCaptions({
    mode,
    position,
    zIndex: Z.CAPTIONS,
  });

  // Expose a handle for HUD/state modules
  try { (section as any).__captionsHandle = handle; } catch {}

  try { log('[CAPTIONS] mounting into', reelFrame ? '.reelFrame' : '.section'); } catch {}

  // Reflect stored CC preference to the section before mounting, so CSS can read it.
  const prefOn = (() => {
    const b = readCcPrefBool();
    if (b !== null) return b;
    const legacy = localStorage.getItem("storycore-cc");
    return legacy !== "off";
  })();
  try {
    section.setAttribute("data-cc-available", "1");
    section.setAttribute("data-cc-hidden", prefOn ? "0" : "1");
  } catch {}

  // Mount into container and attach to the provided video
  try { handle.mount(container); } catch {}
  // Sync the handle's mode to the effective preference if not provided via attrs
  try { handle.setMode(mode ?? (prefOn ? "on" : "off")); } catch {}
  try { handle.attachToVideo(video); } catch {}

  if (!rawSrc) {
    try { log('[CAPTIONS] no explicit captions src — auto-probing same-folder .srt'); } catch {}
    try {
      const rawSrc = (video.currentSrc || video.src || '').split('?')[0];
      const srcUrl = new URL(rawSrc, window.location.href);
      const stem = (rawSrc.split('/').pop() || '').replace(/\.(mp4|webm|m4v)$/i, '');
      const pageOrigin = window.location.origin;
      const fallback = stem ? `${baseUrl}/videos/${stem}.srt` : '';

      let sameFolder = srcUrl.href.replace(/\.(mp4|webm|m4v)$/i, '.srt');
      if (srcUrl.origin !== pageOrigin) {
        // Skip CDN probe entirely; go straight to the local fallback if available
        sameFolder = fallback || srcUrl.pathname.replace(/\.(mp4|webm|m4v)$/i, '.srt');
      }

      const probes: string[] = [];
      if (sameFolder) probes.push(sameFolder);
      if (fallback && fallback !== sameFolder) probes.push(fallback);

      const tryNext = (index: number) => {
        if (index >= probes.length) return;
        handle.loadIfExists(probes[index]).then((ok) => {
          if (!ok) tryNext(index + 1);
        }).catch(() => tryNext(index + 1));
      };
      tryNext(0);
    } catch {}
    return handle;
  }

  // Resolve to absolute URL if necessary.
  const resolveUrl = (s: string) => {
    if (/^https?:\/\//i.test(s) || s.startsWith('/')) { try { log('[CAPTIONS] resolveUrl absolute', s); } catch {}; return s; }
    // New logic: if input has no path and matches a video basename, build same-folder .srt path
    try {
      const inputName = s.split('/').pop() || s;
      const videoUrl = new URL(video.src, window.location.href);
      const videoPathParts = videoUrl.pathname.split('/');
      const videoFileName = videoPathParts.pop() || '';
      const videoBaseName = videoFileName.replace(/\.[^/.]+$/, '');
      if (!s.includes('/') && inputName.toLowerCase().startsWith(videoBaseName.toLowerCase())) {
        const sameFolderSrt = videoUrl.origin + videoUrl.pathname.replace(/\/[^\/]*$/, '/') + videoBaseName + '.srt';
        log('[CAPTIONS] resolveUrl same-folder .srt path', sameFolderSrt);
        return sameFolderSrt;
      }
    } catch (e) {
      // ignore errors here
    }
    // Fallback: assume author keeps captions alongside videos folder
    const resolved = `${baseUrl}/videos/${s}`;
    try { log('[CAPTIONS] resolveUrl relative', { baseUrl, input: s, resolved }); } catch {}
    return resolved;
  };
  const url = resolveUrl(rawSrc);
  const lower = rawSrc.toLowerCase();

  try { log('[CAPTIONS] resolved URL', { url, ext: lower.split('.').pop() }); } catch {}

  if (lower.endsWith(".vtt")) {
    // Attach a native track so TextTrack API can drive our overlay too.
    try {
      // Avoid duplicating an identical track if one exists already
      const existing = Array.from(video.querySelectorAll("track")).find(t => (t as HTMLTrackElement).src === url);
      if (!existing) {
        const track = document.createElement("track");
        track.kind = "subtitles";
        track.label = "captions";
        track.srclang = "en";
        (track as any).default = true;
        track.src = url;
        video.appendChild(track);
      }
      // Ensure the first captions/subtitles track is shown
      const tt = Array.from(video.textTracks || []).find(t => t.kind === "subtitles" || t.kind === "captions");
      if (tt) tt.mode = "showing";
    } catch {}
    try { log('[CAPTIONS] .vtt track attached via <track>:', url); } catch {}
    return handle;
  }

  if (lower.endsWith(".srt")) {
    try { log('[CAPTIONS] .srt requested via explicit src:', url); } catch {}
    // Use our SRT parser/loop
    handle.loadSRT(url).catch(() => {});
    return handle;
  }

  try { log('[CAPTIONS] unknown extension — attempting HEAD/SRT at', url); } catch {}
  // Unknown extension: try SRT fallback (HEAD is inside loadIfExists)
  handle.loadIfExists(url).catch(() => {});
  return handle;
}
